import {
  ClientUserMfaStatusSchemaType,
  PaginatedResponseSchema_ClientUserProfileListSchema_Type,
} from '@/types/portal-api.generated'
import { GetQueryParams, portalApiClient } from '@/utilities/api'

type GetUsersParams = GetQueryParams<'get_client_user_list_v2_clients_users_get'>
type ExportUsersParams = GetQueryParams<'export_client_users_csv_v2_clients_users_export_get'>
type ClientUserMfaParams = {
  user_id: number
  partner_id: string
  partner_ids?: string[] | null
}

export async function getUsers(queries: GetUsersParams): Promise<{
  error: Error | null
  data: PaginatedResponseSchema_ClientUserProfileListSchema_Type | null
}> {
  queries.search_string = queries.search_string?.trim()

  try {
    const data = await portalApiClient.get_client_user_list_v2_clients_users_get({ queries })

    return { error: null, data }
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Failed to fetch users', { cause: e })
    return { error, data: null }
  }
}

export async function deleteUser(params: { partner_id: string; email: string }): Promise<{
  error: Error | null
}> {
  try {
    await portalApiClient.delete_client_user_v2_clients_users_delete(params)
    return { error: null }
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Failed to delete user', { cause: e })
    return { error }
  }
}

export async function updateUser(params: {
  partner_id: string
  email: string
  first_name: string
  last_name: string
}): Promise<{ error: Error | null }> {
  try {
    await portalApiClient.update_profile_v2_clients_users_me_patch(params)
    return { error: null }
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Failed to update user', { cause: e })
    return { error }
  }
}

export async function exportUsers(queries: ExportUsersParams): Promise<{
  error: Error | null
  data: string | null
}> {
  queries.search_string = queries.search_string?.trim()

  try {
    const data = await portalApiClient.export_client_users_csv_v2_clients_users_export_get({
      queries,
    })

    return { error: null, data: data as string }
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Failed to export users', { cause: e })
    return { error, data: null }
  }
}

export async function getClientUserMfaStatus(
  queries: ClientUserMfaParams,
): Promise<ClientUserMfaStatusSchemaType> {
  const data = await portalApiClient.get_client_user_mfa_status_v2_clients_users_mfa_status_get({
    queries,
  })
  return data as ClientUserMfaStatusSchemaType
}

export async function resetClientUserMfa(queries: ClientUserMfaParams): Promise<void> {
  await portalApiClient.reset_client_user_mfa_v3_auth_client_reset_mfa_post(undefined, { queries })
}

type ImpersonateUserResponse = {
  user_id: number
  member_id: string
  session_jwt: string
  session_token: string
}

// POST /v2/clients/users/{user_id}/impersonate is not in the published OpenAPI spec yet
// (CP-1218). Keep this axios wrapper until `pnpm run generate:portal-api-types` can emit
// a Zodios method; map failures with `portal_error_code` only.
export async function impersonateUser(userId: number): Promise<ImpersonateUserResponse> {
  const { data } = await portalApiClient.axios.post<ImpersonateUserResponse>(
    `/v2/clients/users/${userId}/impersonate`,
  )
  return data
}

const clientUsersRepository = {
  getUsers,
  deleteUser,
  updateUser,
  exportUsers,
  getClientUserMfaStatus,
  resetClientUserMfa,
  impersonateUser,
}
export default clientUsersRepository
